<template>
  <div class="td-board">
    <!-- ── Header ───────────────────────────────────────────── -->
    <header class="td-board__head">
      <div class="td-board__head-left">
        <div class="td-board__icon">
          <feather-icon icon="CheckSquareIcon" size="18"/>
        </div>
        <div>
          <h3 class="td-board__title">{{ msg('todolist') || 'To-do list' }}</h3>
          <p class="td-board__subtitle">{{ msg('Get things done, one item at a time.') }}</p>
        </div>
      </div>

      <div v-if="todo.length" class="td-progress">
        <div class="td-progress__numbers">
          <span class="td-progress__done">{{ doneCount }}</span>
          <span class="td-progress__sep">/</span>
          <span class="td-progress__total">{{ todo.length }}</span>
        </div>
        <div class="td-progress__bar">
          <div class="td-progress__bar-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
      </div>
    </header>

    <!-- ── Filter pills ────────────────────────────────────── -->
    <div v-if="todo.length" class="td-filters">
      <button
          v-for="f in filters"
          :key="f.key"
          class="td-filter"
          :class="{ active: activeFilter === f.key }"
          @click="activeFilter = f.key"
      >
        {{ f.label }}
        <span class="td-filter__count">{{ f.count }}</span>
      </button>
    </div>

    <!-- ── List ─────────────────────────────────────────────── -->
    <transition-group v-if="visibleList.length" name="td-flip" tag="ul" class="td-list">
      <li
          v-for="item in visibleList"
          :key="item.id"
          class="td-item"
          :class="{ 'td-item--done': item.done }"
      >
        <button
            class="td-checkbox"
            :class="{ checked: item.done }"
            :aria-label="item.done ? msg('Mark as not done') : msg('Mark as done')"
            @click="markAsDoneOrUndone(item)"
        >
          <feather-icon v-if="item.done" icon="CheckIcon" size="14"/>
        </button>

        <div class="td-item__body">
          <div class="td-item__title-row">
            <span class="td-item__title">{{ item.label }}</span>
            <span v-if="item.tag" class="td-item__tag">
              <feather-icon icon="TagIcon" size="11" class="mr-25"/>{{ item.tag }}
            </span>
          </div>
          <p v-if="item.description" class="td-item__desc">{{ item.description }}</p>
        </div>

        <div class="td-item__actions">
          <button
              class="td-action td-action--danger"
              :aria-label="msg('Delete')"
              v-b-tooltip
              :title="msg('Delete')"
              @click="deleteItemFromList(item)"
          >
            <feather-icon icon="Trash2Icon" size="15"/>
          </button>
        </div>
      </li>
    </transition-group>

    <!-- ── Empty states ─────────────────────────────────────── -->
    <div v-else-if="todo.length" class="td-empty td-empty--filtered">
      <feather-icon icon="FilterIcon" size="22" class="mb-50"/>
      <div>{{ msg('No items match this filter.') }}</div>
    </div>
    <div v-else class="td-empty">
      <div class="td-empty__icon"><feather-icon icon="CheckSquareIcon" size="28"/></div>
      <h4>{{ msg('Your todo list is empty') }}</h4>
      <p>{{ msg('Add the first task using the form below.') }}</p>
    </div>

    <!-- ── Add task ─────────────────────────────────────────── -->
    <div class="td-add" :class="{ 'td-add--open': addOpen }">
      <button v-if="!addOpen" class="td-add__trigger" @click="addOpen = true">
        <feather-icon icon="PlusIcon" size="16" class="mr-50"/>
        {{ msg('Add task') }}
      </button>

      <div v-else class="td-add__panel">
        <div class="td-add__panel-head">
          <span class="td-add__panel-title">
            <feather-icon icon="PlusCircleIcon" size="15" class="mr-50"/>{{ msg('New task') }}
          </span>
          <button class="td-add__close" @click="closeAdd">
            <feather-icon icon="XIcon" size="14"/>
          </button>
        </div>

        <b-form-group :label="msg('Title')" label-for="titleInput" class="mb-1">
          <b-form-input
              id="titleInput"
              v-model="newitem"
              :placeholder="msg('What needs to be done?')"
              @input="validateForm"
              @keydown.enter="onTitleEnter"
          />
        </b-form-group>

        <b-form-group :label="msg('Description')" label-for="descriptionInput" class="mb-1">
          <b-textarea
              id="descriptionInput"
              v-model="newDescription"
              rows="2"
              :placeholder="msg('Add more details (optional)…')"
              @input="validateForm"
          />
        </b-form-group>

        <b-form-group :label="msg('Tag')" label-for="realEstate-branch" class="mb-1">
          <v-select
              :dir="isRTL ? 'rtl' : 'ltr'"
              :clearable="true"
              v-model="newTag"
              :options="tagOptions"
              :placeholder="msg('Pick a tag…')"
              class="w-100"
          />
        </b-form-group>

        <div class="td-add__actions">
          <b-button variant="outline-secondary" size="sm" class="mr-50" @click="closeAdd">
            {{ msg('cancel') }}
          </b-button>
          <b-button
              :disabled="!isFormValid"
              size="sm"
              variant="primary"
              v-b-tooltip
              :title="msg('Save item')"
              @click="addItem"
          >
            <feather-icon icon="PlusIcon" size="14" class="mr-25"/>
            {{ msg('Add') }}
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions} from "vuex";
import useAppConfig from '@core/app-config/useAppConfig'

export default {
  name: 'TodoList',
  setup() {
    const {skin} = useAppConfig()
    const currentSkin = skin.value
    return {currentSkin}
  },
  data() {
    return {
      isFormValid: false,
      newitem: '',
      newDescription: '',
      newTag: '',
      sortByStatus: false,
      todo: [],
      activeFilter: 'all',
      addOpen: false,
      tagOptions: [
        'Online setup',
        'Airbnb',
        'Booking',
        'Pricelabs',
        'Expedia',
        'Agoda',
        'Immoscout24',
        'Möbel',
        'Accessoires',
        'Ausstattung',
        'Online Bestellung',
        'Hostaway',
        'Küchenausstattung (Geschirr, Töpfe/Pfannen und Besteck)',
        'Bad-Essentials',
        'Luxuriöse Produkte',
        'Renovierung',
        'Aufbauen',
        'Reparieren',
        'Internet',
        'Hand/Badetücher',
        'Bettwäsche',
        'Küchen Elektrogeräte',
        'Installationsarbeiten',
        'Vorhänge',
      ],
    };
  },
  watch: {
    currentSkin: {
      handler: function (val, oldVal) {
        console.log('skin changed from ', oldVal, ' to ', val)
      },
      deep: true,
    },
  },
  created() {
    this.todo = this.value.todoList || [];
    console.log('skin: ', this.currentSkin)
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    todoBuilding: {
      type: Boolean,
      default: false
    },
  },
  computed: {
    todoList() {
      if (!this.sortByStatus) {
        return this.todo;
      }
      const doneArray = this.todo.filter((item) => item.done);
      const notDoneArray = this.todo.filter((item) => !item.done);
      return [...notDoneArray, ...doneArray];
    },
    doneCount() {
      return this.todo.filter(t => t.done).length;
    },
    pendingCount() {
      return this.todo.length - this.doneCount;
    },
    progressPercent() {
      if (this.todo.length === 0) return 0;
      return Math.round((this.doneCount / this.todo.length) * 100);
    },
    filters() {
      return [
        {key: 'all', label: this.msg('All') || 'All', count: this.todo.length},
        {key: 'active', label: this.msg('Active') || 'Active', count: this.pendingCount},
        {key: 'done', label: this.msg('Done') || 'Done', count: this.doneCount},
      ];
    },
    visibleList() {
      const list = this.todoList;
      if (this.activeFilter === 'active') return list.filter(t => !t.done);
      if (this.activeFilter === 'done') return list.filter(t => t.done);
      return list;
    },
  },
  methods: {
    ...mapActions("realEstate", ["handleTodoEvent",]),
    ...mapActions("building", ["handleTodoEventBuilding",]),
    validateForm() {
      this.isFormValid = this.newitem !== '' && this.newDescription !== '';
    },
    addItem() {
      this.todo.push({
        id: Math.floor(Math.random() * 9999) + 10,
        label: this.newitem,
        description: this.newDescription,
        tag: this.newTag,
        done: false
      });

      let item = {};
      item.label = this.newitem;
      item.description = this.newDescription;
      item.tag = this.newTag;
      this.handleEventTodoList('add', item);

      this.newitem = '';
      this.newDescription = '';
      this.newTag = '';
      this.isFormValid = false;
      this.addOpen = false;
    },
    onTitleEnter(e) {
      if (this.isFormValid) {
        e.preventDefault();
        this.addItem();
      }
    },
    closeAdd() {
      this.addOpen = false;
      this.newitem = '';
      this.newDescription = '';
      this.newTag = '';
      this.isFormValid = false;
    },
    markAsDoneOrUndone(item) {
      item.done = !item.done;
      this.handleEventTodoList('update', item);
    },
    deleteItemFromList(item) {
      this.handleEventTodoList('delete', item);
      const index = this.todo.indexOf(item);
      this.todo.splice(index, 1);
    },
    handleEventTodoList(event, todo) {
      if (this.todoBuilding)
        this.handleTodoEventBuilding({id: this.value.id, event: event, item: todo});
      else
        this.handleTodoEvent({id: this.value.id, event: event, item: todo});
    },
  }
};
</script>

<style scoped lang="scss">
$td-primary: #7367f0;
$td-primary-dark: #5e50ee;
$td-success: #28c76f;
$td-danger: #ea5455;
$td-radius: 14px;
$td-radius-lg: 16px;
$td-border: rgba(60, 60, 70, 0.08);
$td-border-dark: rgba(255, 255, 255, 0.06);

* {
  box-sizing: border-box;
}

.td-board {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
}

/* ── Header ─────────────────────────────────────────────── */
.td-board__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 4px 4px 18px 4px;
  border-bottom: 1px solid $td-border;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.td-board__head-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.td-board__icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(135deg, $td-primary 0%, $td-primary-dark 100%);
  color: white;
  box-shadow: 0 6px 16px rgba(115, 103, 240, 0.32);
  flex-shrink: 0;
}

.td-board__title {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
}

.td-board__subtitle {
  font-size: 0.78rem;
  color: var(--secondary);
  margin: 2px 0 0 0;
}

/* ── Progress ───────────────────────────────────────────── */
.td-progress {
  min-width: 180px;
  text-align: right;
}

.td-progress__numbers {
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--secondary);
  margin-bottom: 6px;
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
}

.td-progress__done {
  font-size: 1.1rem;
  font-weight: 800;
  color: $td-success;
}

.td-progress__sep {
  opacity: 0.4;
}

.td-progress__bar {
  width: 100%;
  height: 6px;
  background: rgba(115, 103, 240, 0.08);
  border-radius: 999px;
  overflow: hidden;
}

.td-progress__bar-fill {
  height: 100%;
  background: linear-gradient(90deg, $td-primary 0%, $td-success 100%);
  border-radius: 999px;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* ── Filters ────────────────────────────────────────────── */
.td-filters {
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.td-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid $td-border;
  background: transparent;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--secondary);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(115, 103, 240, 0.06);
    border-color: rgba(115, 103, 240, 0.2);
    color: $td-primary;
  }

  &.active {
    background: $td-primary;
    border-color: $td-primary;
    color: white;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.32);
  }
}

.td-filter__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  background: rgba(115, 103, 240, 0.12);
  color: $td-primary;
  font-size: 0.7rem;
  font-weight: 800;
}

.td-filter.active .td-filter__count {
  background: rgba(255, 255, 255, 0.22);
  color: white;
}

/* ── List ───────────────────────────────────────────────── */
.td-list {
  list-style: none;
  margin: 0 0 16px 0;
  padding: 0;
}

.td-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 8px;
  background: var(--card-bg, white);
  border: 1px solid $td-border;
  border-radius: $td-radius;
  transition: all 0.18s ease;

  &:hover {
    border-color: rgba(115, 103, 240, 0.3);
    box-shadow: 0 4px 14px rgba(115, 103, 240, 0.08);
    transform: translateY(-1px);

    .td-item__actions { opacity: 1; }
  }

  &--done {
    background: rgba(40, 199, 111, 0.04);
    border-color: rgba(40, 199, 111, 0.18);

    .td-item__title {
      text-decoration: line-through;
      color: var(--secondary);
      opacity: 0.7;
    }

    .td-item__desc { opacity: 0.6; }
  }
}

/* Custom checkbox */
.td-checkbox {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  border: 2px solid rgba(115, 103, 240, 0.35);
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.18s ease;
  margin-top: 1px;

  &:hover {
    border-color: $td-primary;
    background: rgba(115, 103, 240, 0.08);
  }

  &.checked {
    border-color: $td-success;
    background: $td-success;
    box-shadow: 0 4px 10px rgba(40, 199, 111, 0.32);
  }
}

.td-item__body {
  flex: 1;
  min-width: 0;
}

.td-item__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 2px;
}

.td-item__title {
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  word-break: break-word;
}

.td-item__tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: rgba(115, 103, 240, 0.1);
  color: $td-primary;
  white-space: nowrap;
}

.td-item__desc {
  font-size: 0.82rem;
  color: var(--secondary);
  margin: 0;
  line-height: 1.4;
  word-break: break-word;
}

.td-item__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0.6;
  transition: opacity 0.18s ease;
  flex-shrink: 0;
}

.td-action {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--secondary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  &--danger:hover {
    background: rgba(234, 84, 85, 0.12);
    color: $td-danger;
  }
}

/* ── Empty states ───────────────────────────────────────── */
.td-empty {
  text-align: center;
  padding: 36px 20px;
  margin-bottom: 16px;
  border: 1.5px dashed $td-border;
  border-radius: $td-radius;
  color: var(--secondary);

  h4 {
    font-size: 1rem;
    font-weight: 700;
    margin: 8px 0 4px 0;
    color: var(--text);
  }

  p {
    font-size: 0.84rem;
    margin: 0;
  }
}

.td-empty__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(115, 103, 240, 0.08);
  color: $td-primary;
  margin-bottom: 4px;
}

.td-empty--filtered {
  padding: 24px 20px;
  font-size: 0.86rem;
}

/* ── Add task ───────────────────────────────────────────── */
.td-add__trigger {
  width: 100%;
  padding: 14px 16px;
  border: 1.5px dashed rgba(115, 103, 240, 0.3);
  background: rgba(115, 103, 240, 0.03);
  border-radius: $td-radius;
  font-size: 0.9rem;
  font-weight: 700;
  color: $td-primary;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s ease;

  &:hover {
    background: rgba(115, 103, 240, 0.08);
    border-color: $td-primary;
    border-style: solid;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(115, 103, 240, 0.18);
  }
}

.td-add__panel {
  border: 1px solid $td-border;
  border-radius: $td-radius-lg;
  padding: 16px;
  background: var(--card-bg, white);
  animation: tdSlideDown 0.22s ease-out;
}

.td-add__panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.td-add__panel-title {
  display: inline-flex;
  align-items: center;
  font-size: 0.86rem;
  font-weight: 700;
  color: $td-primary;
  letter-spacing: -0.01em;
}

.td-add__close {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: rgba(0, 0, 0, 0.04);
  color: var(--secondary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(234, 84, 85, 0.12);
    color: $td-danger;
  }
}

.td-add__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

@keyframes tdSlideDown {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── List transitions ───────────────────────────────────── */
.td-flip-enter-active,
.td-flip-leave-active {
  transition: all 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

.td-flip-enter {
  opacity: 0;
  transform: translateY(-8px);
}

.td-flip-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.td-flip-move {
  transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);
}

/* ── Dark mode ──────────────────────────────────────────── */
.dark-layout {
  .td-board__head { border-bottom-color: $td-border-dark; }

  .td-item,
  .td-add__panel {
    background: #283046;
    border-color: $td-border-dark;
  }

  .td-item:hover {
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.32);
  }

  .td-item--done {
    background: rgba(40, 199, 111, 0.08);
    border-color: rgba(40, 199, 111, 0.22);
  }

  .td-filter,
  .td-empty,
  .td-add__trigger {
    border-color: $td-border-dark;
  }

  .td-action:hover {
    background: rgba(255, 255, 255, 0.06);
  }

  .td-add__close {
    background: rgba(255, 255, 255, 0.06);

    &:hover {
      background: rgba(234, 84, 85, 0.18);
    }
  }
}

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 600px) {
  .td-board__head {
    flex-direction: column;
    align-items: stretch;
  }

  .td-progress {
    min-width: 0;
    text-align: left;
  }

  .td-item {
    padding: 12px;
  }

  .td-item__actions {
    opacity: 1;
  }
}
</style>
